const STORAGE_KEY = 'telemetry-session';
export const IDLE_TIMEOUT_MS = 30 * 60 * 1000;

export interface TelemetrySession {
  id: string;
  startedAt: number;
  lastSeenAt: number;
  activeMs: number;
}

export interface ResolvedSession extends TelemetrySession {
  isNew: boolean;
}

// Blocked site data makes the global getter throw SecurityError, which `typeof` won't suppress.
function storage(): Storage | undefined {
  try {
    return typeof sessionStorage === 'undefined' ? undefined : sessionStorage;
  } catch {
    return undefined;
  }
}

function createId(): string {
  try {
    if (typeof crypto?.randomUUID === 'function') return crypto.randomUUID();
  } catch {
    // randomUUID is secure-context only
  }
  return `s-${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 12)}`;
}

function read(): TelemetrySession | undefined {
  try {
    const parsed: unknown = JSON.parse(
      storage()?.getItem(STORAGE_KEY) ?? 'null',
    );
    if (typeof parsed !== 'object' || parsed === null) return undefined;
    const stored = parsed as Record<string, unknown>;
    if (typeof stored.id !== 'string' || !stored.id) return undefined;
    if (
      typeof stored.startedAt !== 'number' ||
      typeof stored.lastSeenAt !== 'number'
    ) {
      return undefined;
    }
    return {
      id: stored.id,
      startedAt: stored.startedAt,
      lastSeenAt: stored.lastSeenAt,
      activeMs: typeof stored.activeMs === 'number' ? stored.activeMs : 0,
    };
  } catch {
    return undefined;
  }
}

function write(session: TelemetrySession) {
  try {
    storage()?.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch {
    // ignore quota / disabled storage
  }
}

let current: TelemetrySession | undefined;

/** Returns the live session, rolling it over after 30 minutes of inactivity. */
export function resolveSession(now: number): ResolvedSession {
  current ??= read();

  const isNew = !current || now - current.lastSeenAt > IDLE_TIMEOUT_MS;
  if (isNew || !current) {
    current = { id: createId(), startedAt: now, lastSeenAt: now, activeMs: 0 };
  }

  current = { ...current, lastSeenAt: now };
  write(current);
  return { ...current, isNew };
}

/** Reads the session without rolling it over: safe during unload. */
export function peekSession(): TelemetrySession | undefined {
  current ??= read();
  return current;
}

export function addActiveTime(elapsedMs: number) {
  if (!current || elapsedMs <= 0) return;
  current = { ...current, activeMs: current.activeMs + elapsedMs };
  write(current);
}
